"use client"

import { useAutoAnimate } from "@formkit/auto-animate/react"
import { useScroll } from "ahooks"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { useCartState } from "src/checkout/hooks/useCart"
import { NpButton } from "src/components/np-design/components/button/NpButton"
import { NpSpinner } from "src/components/np-design/components/NpSpinner"
import { NpCartSidebarAccessories } from "src/components/np-design/sections/cart-sidebar/NpCartSidebarAccessories"
import { NpCartSidebarFooter } from "src/components/np-design/sections/cart-sidebar/NpCartSidebarFooter"
import { NpFreeShippingProgress } from "src/components/np-design/sections/cart-sidebar/NpFreeShippingProgress"
import { NpSidebarCartItems } from "src/components/np-design/sections/cart-sidebar/NpSidebarCartItems"
import { useRecommendedAccessories } from "src/components/np-design/sections/cart-sidebar/recommendedAccessories"
import { setCartSidebarVisible, useCartSidebarVisibile } from "src/components/np-design/sections/cart-sidebar/state"
import { Divider } from "src/components/styled/Divider"
import { useBackwardForwardCacheRestored } from "src/hooks/useBackwardForwardCacheRestored"
import { useEventCallback } from "src/hooks/useEventCallback"
import { useKeydown } from "src/hooks/useKeydown"
import { usePreventBodyScroll } from "src/hooks/usePreventBodyScroll"

export const NpCartSidebar = () => {
  const { loading, data: cart } = useCartState()
  const accessories = useRecommendedAccessories()
  const visible = useCartSidebarVisibile()
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollPosition = useScroll(scrollRef)
  const [animating, setAnimating] = useState(false)
  const [animationParent] = useAutoAnimate()

  const transitionDuration = 300
  const atTop = !scrollPosition?.top
  const scrollTop = Math.ceil(scrollPosition?.top ?? 0)
  const clientHeight = scrollRef.current?.clientHeight ?? 0
  const scrollHeight = scrollRef.current?.scrollHeight ?? 0
  const scrollBottom = scrollTop + clientHeight
  const scrollShadowOpacity = Math.min(1, Math.max(0, (scrollHeight - scrollBottom) / 80))

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    setAnimating(true)
    setTimeout(() => {
      setAnimating(false)
    }, transitionDuration)
  }, [visible])

  const hide = useEventCallback(() => {
    setCartSidebarVisible(false)
    setAnimating(true)
  })

  usePreventBodyScroll(visible || animating)
  useKeydown("Escape", hide)
  useBackwardForwardCacheRestored(hide)

  const empty = !cart?.items.length

  return (
    <div
      className={clsx("z-add-to-cart relative size-full transition-opacity", {
        "pointer-events-none opacity-0": !visible,
        "opacity-100": visible,
      })}
      style={{ transitionDuration: `${transitionDuration}ms` }}
    >
      {(visible || animating) && (
        <div className="fixed inset-0 flex">
          <button
            type="button"
            tabIndex={-1}
            onClick={hide}
            className="absolute inset-0 size-full cursor-default bg-black/30 sm:block"
          />

          <aside
            aria-label="Cart"
            className="z-1 ml-auto flex size-full max-w-full flex-col bg-white shadow-lg sm:max-w-lg sm:min-w-lg"
          >
            <header
              className={clsx("flex min-h-20 items-center justify-between gap-4 pr-4 pl-6", {
                shadow: !atTop || empty,
              })}
            >
              <h2 className="text-h2-np m-0">Dein Warenkorb</h2>
              <NpButton
                iconNameLeft="close"
                variant="ghost"
                color="gray75"
                size="md"
                onClick={hide}
                title="Schließen"
              />
            </header>

            {loading ? (
              <div className="flex size-full items-center justify-center">
                <NpSpinner />
              </div>
            ) : empty ? (
              <div className="flex size-full flex-col items-center justify-center gap-4 p-6 text-center">
                <div className="font-medium">Dein Warenkorb ist leer.</div>
                <NpButton onClick={hide}>Schließen</NpButton>
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="size-full overflow-y-auto">
                  <NpFreeShippingProgress />
                  <NpSidebarCartItems />
                  <Divider color="green" />
                  <NpCartSidebarAccessories accessories={accessories} />
                  <div className="flex-1" />
                </div>

                <div className="relative">
                  <div
                    className="to-np-gray10 pointer-events-none absolute right-0 bottom-0 left-0 h-20 w-full border-b border-gray-200 bg-gradient-to-b from-transparent transition-opacity"
                    style={{ opacity: scrollShadowOpacity }}
                  />
                </div>

                <NpCartSidebarFooter />
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  )
}
